import * as THREE from 'three'
import type { Position } from '../../stores/gameStore'
import { axialToPixel, generateBoardPositions } from '../../utils/board'

export const HEX_SIZE = 1.08
export const MARBLE_RADIUS = 0.56
export const WELL_RADIUS = 0.46
export const BOARD_THICKNESS = 0.5
export const BOARD_TOP_Y = 0.14
// Marble rests on the board surface, nestled a hair into its carved dimple.
export const MARBLE_Y = BOARD_TOP_Y + MARBLE_RADIUS * 0.9

export const BOARD_POSITIONS = generateBoardPositions()

const MAX_HYPOT = Math.max(
  ...BOARD_POSITIONS.map((pos) => {
    const { x, y } = axialToPixel(pos.q, pos.r, HEX_SIZE)
    return Math.hypot(x, y)
  }),
)

// Full wooden disc radius (includes the rim), and the flat playing surface radius.
export const BOARD_RADIUS = MAX_HYPOT + MARBLE_RADIUS * 3.1
export const BOARD_TOP_RADIUS = BOARD_RADIUS * 0.965

export const SCENE_DIAMETER = BOARD_RADIUS * 2.24

// Resolution of the baked board texture atlas.
export const BOARD_TEXTURE_SIZE = 2048

export type MarbleStyleId = 'alley-glass'

export interface MarblePalette {
  base: string
  light: string
  dark: string
  core: string
  coreLight: string
  rim: string
  vein: string
  caustic: string
}

export const MARBLE_STYLE_LABELS: Record<MarbleStyleId, string> = {
  'alley-glass': 'Teahouse Glass',
}

export const MARBLE_PALETTES: Record<MarbleStyleId, Record<number, MarblePalette>> = {
  'alley-glass': {
    1: {
      base: '#bb1f2b',
      light: '#ff7d6b',
      dark: '#3a070b',
      core: '#dc3a34',
      coreLight: '#ffb39c',
      rim: '#ff6a4c',
      vein: '#f7bda6',
      caustic: '#ef5038',
    },
    2: {
      base: '#0e6ea0',
      light: '#5fccf0',
      dark: '#052a3e',
      core: '#1a90bb',
      coreLight: '#b6efff',
      rim: '#58cbf2',
      vein: '#c9edf3',
      caustic: '#33b0e4',
    },
  },
}

export function getMarblePalette(style: MarbleStyleId, player: number) {
  return MARBLE_PALETTES[style]?.[player] ?? MARBLE_PALETTES['alley-glass'][1]
}

export function positionKey(pos: Position): string {
  return `${pos.q},${pos.r}`
}

function noise(pos: Position, salt: number) {
  const value = Math.sin(pos.q * 127.1 + pos.r * 311.7 + salt * 74.7) * 43758.5453123
  return value - Math.floor(value)
}

// Subtle per-hole imperfection so the carved board reads as hand-made (wabi-sabi).
export function getBoardIrregularity(pos: Position) {
  return {
    xOffset: (noise(pos, 1) - 0.5) * 0.05,
    zOffset: (noise(pos, 2) - 0.5) * 0.05,
    wellRadius: WELL_RADIUS * (0.95 + noise(pos, 4) * 0.09),
    ovalX: 0.96 + noise(pos, 6) * 0.07,
    ovalZ: 0.96 + noise(pos, 7) * 0.07,
    rotation: noise(pos, 8) * Math.PI,
  }
}

export function boardToXZ(pos: Position): { x: number; z: number } {
  const { x, y } = axialToPixel(pos.q, pos.r, HEX_SIZE)
  const irregularity = getBoardIrregularity(pos)
  return { x: x + irregularity.xOffset, z: y + irregularity.zOffset }
}

export function boardToWorld(pos: Position, y = MARBLE_Y): THREE.Vector3 {
  const { x, z } = boardToXZ(pos)
  return new THREE.Vector3(x, y, z)
}

// Map a world-space (x, z) on the board surface to a pixel on the baked texture.
// The board top is a circleGeometry(BOARD_TOP_RADIUS) laid flat, so the mapping is
// a straight linear scale about the texture centre.
export function worldToCanvas(x: number, z: number, size = BOARD_TEXTURE_SIZE) {
  const half = size / 2
  return {
    x: half + (x / BOARD_TOP_RADIUS) * half,
    y: half + (z / BOARD_TOP_RADIUS) * half,
  }
}

export function worldLengthToCanvas(length: number, size = BOARD_TEXTURE_SIZE) {
  return (length / BOARD_TOP_RADIUS) * (size / 2)
}

// Let the canvas recover from a WebGL context loss instead of dying: calling
// preventDefault on 'webglcontextlost' is what makes the browser fire a later
// 'webglcontextrestored' (Three then re-inits its GPU resources). Shaky GPU drivers
// — and GPU contention from WebGPU inference — can drop the context on load; without
// this the 3D board goes permanently blank. Wire it up via <Canvas onCreated>.
export function keepContextAlive(gl: THREE.WebGLRenderer): void {
  gl.domElement.addEventListener('webglcontextlost', (event) => event.preventDefault())
}
